import type { Ref } from 'react';
import { Loader2 } from 'lucide-react';
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';
import { Button } from '@/components/ui/button';
import { DialogDescription, DialogTitle } from '@/components/ui/dialog';

interface OnboardingCommunityOfferProps {
  headingRef?: Ref<HTMLHeadingElement>;
  title: string;
  description: string;
  primaryLabel: string;
  secondaryLabel: string;
  pendingLabel: string;
  pending: boolean;
  onPrimary: () => void;
  onSecondary: () => void;
}

export const OnboardingCommunityOffer = ({
  headingRef,
  title,
  description,
  primaryLabel,
  secondaryLabel,
  pendingLabel,
  pending,
  onPrimary,
  onSecondary,
}: OnboardingCommunityOfferProps) => (
  <section className="onboarding-community-offer -m-5 grid min-h-[520px] overflow-hidden bg-foreground text-background sm:-m-9 lg:-m-11 lg:grid-cols-[0.9fr_1.1fr]">
    <div className="flex items-center px-6 py-10 sm:px-10 sm:py-14 lg:px-14 lg:py-16">
      <div className="w-full max-w-xl">
        <div
          aria-hidden="true"
          className="mb-8 h-[3px] w-full max-w-sm bg-[hsl(var(--community-brand))]"
        />
        <DialogTitle
          ref={headingRef}
          tabIndex={-1}
          className="max-w-xl text-4xl font-semibold leading-[0.98] tracking-[-0.045em] text-[hsl(var(--community-brand))] focus:outline-none sm:text-5xl"
        >
          {title}
        </DialogTitle>
        <DialogDescription className="sr-only">{description}</DialogDescription>

        <div className="mt-9 grid max-w-sm gap-3">
          <Button
            type="button"
            size="lg"
            disabled={pending}
            onClick={onPrimary}
            className="justify-start bg-[hsl(var(--community-brand))] font-semibold text-[hsl(var(--community-action-foreground))] hover:bg-[hsl(var(--community-brand-hover))] focus-visible:ring-[hsl(var(--community-brand))] sm:min-w-56"
          >
            {pending ? (
              <Loader2 className="h-5 w-5 animate-spin motion-reduce:animate-none" />
            ) : (
              <WhatsAppIcon className="h-5 w-5" />
            )}
            {pending ? pendingLabel : primaryLabel}
          </Button>
          <Button
            type="button"
            size="lg"
            disabled={pending}
            onClick={onSecondary}
            className="justify-start bg-[hsl(var(--community-muted))] font-semibold text-[hsl(var(--community-secondary-foreground))] hover:bg-[hsl(var(--community-muted-hover))] focus-visible:ring-[hsl(var(--community-muted))] sm:min-w-36"
          >
            {secondaryLabel}
          </Button>
        </div>
      </div>
    </div>

    <div className="flex min-h-72 items-center justify-center border-t border-background/10 px-6 pb-10 pt-2 sm:px-10 sm:pb-14 lg:min-h-0 lg:border-l lg:border-t-0 lg:p-14">
      <img
        src="/images/the-clippers-logo.png"
        alt="The Clippers"
        className="h-auto max-h-[440px] w-full max-w-md object-contain"
      />
    </div>
  </section>
);
